import { useState, useEffect } from "react";
import { Search, User, Loader2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useUserManagement } from "@/hooks/useUserManagement";
import { UserSummary } from "@/types/userDetails";
import { Pagination } from "@/components/ui/pagination";
import { nativeNestedSheetDialogProps } from "@/lib/native/nativeNestedSheetProps";
import { cn } from "@/lib/utils";

interface SelectSellerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectSeller: (seller: UserSummary) => void;
}

// Helper function to get user type label and badge styling
const getUserTypeBadge = (role: string) => {
  switch (role?.toLowerCase()) {
    case "individual":
      return {
        label: "Privatperson",
        className:
          "bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/80 dark:text-blue-300 dark:border-blue-700",
      };
    case "dealer":
      return {
        label: "Handlare",
        className:
          "bg-purple-100 text-purple-700 border-purple-200 dark:bg-purple-900/80 dark:text-purple-300 dark:border-purple-700",
      };
    case "business":
      return {
        label: "Företag",
        className:
          "bg-green-100 text-green-700 border-green-200 dark:bg-green-900/80 dark:text-green-300 dark:border-green-700",
      };
    default:
      return {
        label: role || "Okänd",
        className:
          "bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-900/80 dark:text-gray-300 dark:border-gray-700",
      };
  }
};

export const SelectSellerDialog = ({
  open,
  onOpenChange,
  onSelectSeller,
}: SelectSellerDialogProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeSearchQuery, setActiveSearchQuery] = useState("");

  const limit = 10;
  const { isLoadingUsers, users, totalPages, totalCount } = useUserManagement(
    undefined,
    currentPage,
    limit,
    activeSearchQuery || undefined, // Pass undefined when empty to show all users
    undefined, // userType - undefined means all users
    undefined, // userRole
    false, // includeDeleted
    { enabled: open, excludeAdmin: true }, // Only fetch when dialog is open, exclude admin users
  );

  const handleSearch = () => {
    setActiveSearchQuery(searchQuery.trim());
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSelectSeller = (seller: UserSummary) => {
    onSelectSeller(seller);
    onOpenChange(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setActiveSearchQuery("");
      setCurrentPage(1);
    }
  }, [open]);

  const nestedSheetProps = nativeNestedSheetDialogProps(open, { expandable: true });

  const userTypeBadge = (role: string) => {
    const badge = getUserTypeBadge(role);
    return (
      <Badge variant="outline" className={`${badge.className} text-xs`}>
        {badge.label}
      </Badge>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        {...nestedSheetProps}
        className={cn(
          nestedSheetProps.className,
          "max-w-2xl max-h-[80vh] overflow-hidden flex flex-col",
        )}
      >
        <DialogHeader>
          <DialogTitle>Välj säljare</DialogTitle>
          <DialogDescription>
            {activeSearchQuery
              ? `Visar ${users.length} av ${totalCount} användare för "${activeSearchQuery}"`
              : `Visar alla ${totalCount} användare`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
          {/* Search input */}
          <div className="flex gap-2">
            <Input
              placeholder="Sök på användarnamn eller e-post..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="flex-1"
            />
            <Button onClick={handleSearch} disabled={isLoadingUsers}>
              {isLoadingUsers ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Search className="h-4 w-4" />
              )}
              Sök
            </Button>
          </div>

          {/* Results */}
          <div className="flex-1 overflow-auto">
            {isLoadingUsers ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : users.length > 0 ? (
              <div className="space-y-2">
                {users.map((user) => {
                  const badge = getUserTypeBadge(user.role);
                  const companyName = (user as any).company?.name;
                  return (
                    <div
                      key={user._id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => handleSelectSeller(user)}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <User className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium truncate">
                              {`${user.firstName || ""} ${user.lastName || ""}`.trim() ||
                                user.email}
                            </span>
                            {userTypeBadge(user.role)}
                          </div>
                          <div className="text-sm text-muted-foreground truncate">
                            {user.email}
                          </div>
                          {companyName && (
                            <div className="flex items-center gap-1 mt-1 text-sm text-muted-foreground">
                              <Building2 className="h-3 w-3" />
                              <span className="truncate">{companyName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-shrink-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectSeller(user);
                        }}
                      >
                        Välj
                      </Button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                {activeSearchQuery
                  ? "Inga användare hittades med den sökningen"
                  : "Inga användare hittades"}
              </div>
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pt-2 border-t">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalCount}
                itemsPerPage={limit}
                onPageChange={handlePageChange}
                onItemsPerPageChange={() => {}}
                itemsPerPageOptions={[10]}
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
